import { MOBILE_SCREEN_SIZE } from "@brandclub/common-ui";
import { Box } from "@mui/material";
import { useSelector } from "@xstate/react";
import { AnimatePresence, motion } from "framer-motion";
import _startCase from "lodash/startCase";

import CardBrandIcon from "../../../../pages/AppPages/Dashboard/Wallet/CardBrandIcon";
import { StoreAnimatedHeightDiv } from "../../../../StoreComponents/StoreAnimatedHeightDiv";
import { useFastCheckoutContext } from "../../../CheckoutMachine";

import Stripe from "stripe";
import {
  AsyncCheckoutOrderType,
  UserInteractionType,
  UserRetailerCheckoutOrder,
} from "../../../types";
import { UserRetailerCheckoutPaymentVerificationCommunicationResponse } from "../../../CheckoutMachine/types/FastCheckoutMachine";
import { PaymentMethodDisplay } from "../../CheckoutV2/CheckoutForm/components/PaymentMethodDisplay";
import InfoBox from "../InfoBox";
import { StoreErrorV2 } from "@/components/StoreComponents/StoreError";
import FmdBadRoundedIcon from "@mui/icons-material/FmdBadRounded";
import { PaymentVerificationPopup } from "../../CheckoutV2/CheckoutForm/components/payment/PaymentVerificationPopup";
import { useState } from "react";

const isPaymentMethodMatched = (
  paymentMethod: UserRetailerCheckoutOrder["paymentMethod"],
  stripePaymentMethod: Stripe.PaymentMethod | undefined
) => {
  return (
    paymentMethod &&
    stripePaymentMethod &&
    paymentMethod?.cardNumberEnding === stripePaymentMethod?.card?.last4
  );
};

const PaymentMethod = ({
  paymentMethod,
  stripePaymentMethod,
  orderType,
}: {
  paymentMethod: UserRetailerCheckoutOrder["paymentMethod"];
  stripePaymentMethod: Stripe.PaymentMethod | undefined;
  onAddPaymentMethod: () => void;
  orderType?: AsyncCheckoutOrderType;
}) => {
  const { actorRef } = useFastCheckoutContext();
  const paymentError = useSelector(
    actorRef,
    (state) => state.context.sectionState.payment.errorMessage
  );
  const isGuest = orderType === AsyncCheckoutOrderType.GUEST;

  if (!paymentMethod) {
    if (stripePaymentMethod) {
      return <PaymentMethodDisplay paymentMethod={stripePaymentMethod} />;
    }
    return (
      <InfoBox message="Please add a payment method to your retailer account." />
    );
  }

  if (isGuest && stripePaymentMethod) {
    return <PaymentMethodDisplay paymentMethod={stripePaymentMethod} />;
  }

  const paymentMethodMatched = isPaymentMethodMatched(
    paymentMethod,
    stripePaymentMethod
  );

  const cardBrand = paymentMethod?.cardIssuer?.toLocaleLowerCase();
  const cardNumberEnding = paymentMethod?.cardNumberEnding;

  return (
    <div className="info_box">
      <div className="info_row">
        <CardBrandIcon className="icon" alt="icon" cardBrand={cardBrand} />
        {_startCase(paymentMethod?.cardIssuer || "")} ending in{" "}
        {cardNumberEnding}
      </div>
      {!paymentMethodMatched && !paymentError && (
        <div
          className="info_row_label"
          style={{
            display: "flex",
            msFlexDirection: "row",
            alignItems: "center",
            gap: "5px",
            marginTop: "5px",
            fontSize: "12px",
            backgroundColor: "#EBFBF6",
            width: "fit-content",
            padding: "6px 10px",
            borderRadius: "4px",
          }}
        >
          <FmdBadRoundedIcon sx={{ fontSize: "16px", color: "#34D39D" }} />
          Your default retailer payment method was used.
        </div>
      )}
    </div>
  );
};
const PaymentSection = () => {
  const { actorRef, send } = useFastCheckoutContext();
  const [paymentVerificationPopupOpen, setPaymentVerificationPopupOpen] =
    useState(true);

  const open = useSelector(
    actorRef,
    (state) => state.context.sectionState.payment.open
  );

  const errorMessage = useSelector(
    actorRef,
    (state) => state.context.sectionState.payment.errorMessage
  );

  // const chevronShow = useSelector(
  //   actorRef,
  //   (state) => state.context.sectionState.payment.chevronShow
  // );

  // const toggleOpen = () => {
  //   if (open) {
  //     send({
  //       type: "ON_SESSION_CLOSE",
  //       data: { sessionToClose: "payment" },
  //     });
  //   } else {
  //     send({
  //       type: "ON_SESSION_OPEN",
  //       data: { sessionToOpen: "payment" },
  //     });
  //   }
  // };

  const userRetailerCheckoutOrder = useSelector(
    actorRef,
    (snapshot) =>
      snapshot.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder
  );

  const userInteractionRequirement =
    userRetailerCheckoutOrder?.userInteractionRequirement;

  const handleSubmitPaymentVerification = (code: string) => {
    const interactionData: UserRetailerCheckoutPaymentVerificationCommunicationResponse =
      {
        value: code,
      };

    send({
      type: "SUBMIT_USER_INTERACTION",
      data: {
        interactionType: UserInteractionType.RETAILER_PAYMENT_VERIFICATION,
        interactionData,
      },
    });
  };

  return (
    <Box className="section">
      <div className="section_head" style={{ cursor: "pointer" }}>
        <div className="title">{"Payment Method"}</div>
        {/* {chevronShow ? (
          <ChevronDownIcon
            style={{
              transition: "transform 0.3s",
              width: "33px",
              height: "33px",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
            onClick={toggleOpen}
          ></ChevronDownIcon>
        ) : null} */}
      </div>
      <StoreAnimatedHeightDiv>
        <StoreErrorV2
          errorMessage={errorMessage}
          sx={{
            width: "fit-content",
            marginTop: "2.2px",
            marginBottom: "24px",
            [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
              marginTop: "0px",
              marginBottom: "15px",
            },
          }}
        />
        <PaymentMethod
          paymentMethod={userRetailerCheckoutOrder?.paymentMethod}
          stripePaymentMethod={
            userRetailerCheckoutOrder?.userCheckoutInfo?.stripePaymentMethod
          }
          orderType={userRetailerCheckoutOrder?.orderType}
          onAddPaymentMethod={() => {
            send({
              type: "ON_SESSION_OPEN",
              data: { sessionToOpen: "payment" },
            });
          }}
        />
        <AnimatePresence mode="wait">
          <motion.div
            layout="position"
            key={open ? "view" : "edit"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
          ></motion.div>
        </AnimatePresence>
      </StoreAnimatedHeightDiv>
      {userInteractionRequirement?.type ===
      UserInteractionType.RETAILER_PAYMENT_VERIFICATION ? (
        <PaymentVerificationPopup
          open={paymentVerificationPopupOpen}
          onClose={() => {
            setPaymentVerificationPopupOpen(false);
          }}
          onSave={handleSubmitPaymentVerification}
          displayMessage={userInteractionRequirement?.displayMessage ?? ""}
        />
      ) : null}
    </Box>
  );
};

export default PaymentSection;
